import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import getSelectedTextElementObject from 'editor/src/store/design/selector/getSelectedTextElementObject';
import getStructureIndexesOfSelectedElements from 'editor/src/store/design/selector/getStructureIndexesOfSelectedElements';
import { getTextStyle } from 'editor/src/store/design/util/textStyles';
import isPersonalizationLockElementsAllowed from 'editor/src/store/editor/selector/isPersonalizationLockElementsAllowed';
import { useSelector } from 'editor/src/store/hooks';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import TextPersonalizationPromotionBanner from 'editor/src/component/Banners/TextPersonalizationPromotionBanner';
import ContentBlock from 'editor/src/component/DesktopSidebar/TabContents/Elements/ContentBlock';
import { TabContentProps } from 'editor/src/component/DesktopSidebar/TabContents/index';
import TabContentHeader from 'editor/src/component/DesktopSidebar/TabContents/TabContentHeader';
import PersonalizationOptions from 'editor/src/component/TopMenuDesktop/buttons/ButtonPersonalization/PersonalizationOptions';

import ButtonAddText from './ButtonAddText';
import TextStylesSection from './TextStylesSection';
import TextStyleEditorView from './TextStylesSection/TextStyleEditorView';

function TextsTabContent({ title }: TabContentProps) {
  const { t } = useTranslation();
  const firstSelectedElementAddress = useSelector((state) => getStructureIndexesOfSelectedElements(state)[0]);
  const selectedTextElement = useSelector(getSelectedTextElementObject);
  const personalizationLockElementsAllowed = useSelector(isPersonalizationLockElementsAllowed);

  // GAPI-61895: the style being edited replaces the tab's content (authors only); the list keeps
  // its scroll position for when the author comes back.
  const [editing, setEditing] = useState<{ styleId: string; focusName: boolean }>();
  const editingStyle = useSelector((state) =>
    editing && !isEmbedded ? getTextStyle(state.design.designData, editing.styleId) : undefined,
  );
  const listRef = useRef<HTMLDivElement>(null);
  const listScrollTop = useRef(0);
  const openStyleEditor = useCallback((styleId: string, focusName: boolean) => {
    listScrollTop.current = listRef.current?.scrollTop ?? 0;
    setEditing({ styleId, focusName });
  }, []);
  const closeStyleEditor = useCallback(() => setEditing(undefined), []);
  // The style went away under the editor (deleted, or its creation undone): back to the list.
  const showEditor = !!editingStyle;
  useEffect(() => {
    if (editing && !showEditor) {
      setEditing(undefined);
    }
  }, [editing, showEditor]);
  useLayoutEffect(() => {
    if (!showEditor && listRef.current) {
      listRef.current.scrollTop = listScrollTop.current;
    }
  }, [showEditor]);

  if (editingStyle && editing) {
    return (
      <TextStyleEditorView
        key={editing.styleId}
        style={editingStyle}
        focusName={editing.focusName}
        onClose={closeStyleEditor}
      />
    );
  }

  return (
    <>
      <TabContentHeader title={title || t('editor-text')} />
      <ContentBlock scroll ref={listRef}>
        <ButtonAddText />
        <TextStylesSection onEdit={openStyleEditor} />
        <TextPersonalizationPromotionBanner />
        {!!(selectedTextElement && personalizationLockElementsAllowed) && (
          <div className="mt-3">
            <PersonalizationOptions
              enableSampleElementShortDescription
              toggleSampleElementView
              origin="text_tab"
              addSeparator={false}
              allowPersonalizationControlType="toggle"
              structureIndex={firstSelectedElementAddress}
              element={selectedTextElement}
            />
          </div>
        )}
      </ContentBlock>
    </>
  );
}

export default React.memo(TextsTabContent);
